import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchOrderDetailsFor,
  getSecondaryStatus,
  orderDetailLoading,
  sendOrderIdToCitrus,
  getTransactionStatus,
} from '../redux/orderConfirmation.actions';
import {
  getBasicOrderDetailFromStorage,
  isNewOrderConfirmationApiDeployed,
  transformData,
} from './../utils/orderConfirmation.transform';
import { hydrateReducerWithOrder } from '../redux/orderConfirmation.actions';
import { ORDER_CONFIRMATION_DETAILS_KEY } from './../OrderConfirmation.types';
import { IAppState } from 'config/redux/reducers';
import { IConfirmationOrderDetailsResponse } from 'pages/OrderDetail/orderData.type';

import { triggerRichRelevanceUpdate } from 'pages/Cart/redux/actions/richRelevance.actions';
import { initStorageManager } from 'utils/customStorage.util';

import constants from 'config/constants';
import {
  emitNewPurchaseEvent,
  emitPurchaseEvent,
  transformCitrusData,
} from '../utils/orderConfirmation.utils';
import { trackTransaction } from 'pages/Checkout/redux/actions/order.action';
import { trackTransaction as trackTransactionV3 } from 'pages/Checkout/redux/actions/orderV3.action';
import { useFeature } from '@mafc/appshell';
import { GROWTHBOOK_FLAGS } from 'pages/NewCart/constants/growthbook.constants';
import { IMapString } from 'common/interfaces/common.interface';
import {
  NEW_RELIC_EVENT,
  ORDER_ID,
  TRANSACTION_ID,
} from 'pages/Checkout/constants/checkout.constant';
import { newRelicCustomEvent } from 'utils/newRelicTracker';
import { EPaymentArchitectureTypes } from 'pages/Checkout/redux/actions/paymentArchitecture.action';

const cookies = new Cookies();

enum ERichRelConstants {
  placements = 'purchase_complete_page',
  rrLinkType = 'updateCartTrackingUrl',
}
const sessStorage = initStorageManager(() => sessionStorage);
const storage = initStorageManager(() => localStorage);
export default function() {
  const currentState = useSelector((state: IAppState) => state);
  const { countryConfiguration = {} } = currentState.appConfig;
  const isNewOrderConfirmationApi: boolean = isNewOrderConfirmationApiDeployed(currentState);
  const {
    checkout,
    orderConfirmation,
    orderSummary,
    loyalty,
    appConfig,
    userData = {},
  } = currentState;
  const {
    features: { isSponsoredOrderReportingEnabled = false },
    currencyRate,
  } = appConfig;
  const isRRTrackerUpdated = useRef(false);
  const isOrderConfirmationPage = useRef(false);
  const { orderID: queryOrderId } = useParams<any>();
  const orderID = queryOrderId || storage.getItem(ORDER_ID);
  const {
    orderDetails: { orderFieldMap, orderNumber, orderWsDto },
    nonCardRedirectionURI,
    paymentArchitecture,
  } = checkout;
  const dispatch = useDispatch();
  const adtechCretioEnabled = useFeature(GROWTHBOOK_FLAGS.CRITEO_AD_ENABLED).on;

  useEffect(() => {
    newRelicCustomEvent(NEW_RELIC_EVENT.ORDER_CONFIRMATION, {
      orderId: orderID,
      storageOrderId: storage.getItem(ORDER_ID),
    });
  }, [orderID]);

  const captureCheckoutDetails = () => {
    if (
      (orderFieldMap && orderFieldMap.reference_number) ||
      nonCardRedirectionURI ||
      (orderWsDto && orderNumber)
    ) {
      storage.setItem(
        ORDER_CONFIRMATION_DETAILS_KEY,
        JSON.stringify({
          loyalty,
          // Below info will be used on order confiramtion page, If order details API gets failed
          orderNumber,
          totalPriceWithTax: orderSummary.totalPriceWithTax,
          userData: {
            email: userData.email,
            phone: (userData.defaultAddress && userData.defaultAddress.phone) || '',
            name: userData.firstName,
          },
        })
      );
    }
  };

  const clearSessionStorage = () => cookies.remove(ORDER_CONFIRMATION_DETAILS_KEY);
  const checkSecondaryStatus = () => {
    return storage.getItem(constants.secondaryPaymentCheckStatus) || false;
  };

  const checkPaymentV3Status = () => {
    return !!storage.getItem(TRANSACTION_ID);
  };
  const getCheckoutOrderDetails = (labels: IMapString) => {
    const checkStatus = checkSecondaryStatus();
    const checkStatusV3 = checkPaymentV3Status();
    // if we have to call the apis in parallel then we will remove this before merging
    // const callFunctionObj: { [key: string]: () => void } = {
    //   true: getSecondaryPaymentStatus,
    //   false: retrieveCheckoutDetails,
    // };
    // callFunctionObj[checkStatus]();

    const {
      paymentMethods: { checkStatusFrequency },
    } = countryConfiguration || {
      paymentMethods: {
        checkStatusFrequency: 0,
      },
    };
    if (checkStatus) {
      setTimeout(getSecondaryPaymentStatus, checkStatusFrequency * 1000);
    }

    if (checkStatusV3) {
      setTimeout(getPaymentTransactionStatus, checkStatusFrequency * 1000);
    }
    retrieveCheckoutDetails(labels);
  };

  const getSecondaryPaymentStatus = () => {
    dispatch(
      getSecondaryStatus({
        currentOrderID: orderID,
        // onSuccessCallback: retrieveCheckoutDetails,
      })
    );
  };

  const getPaymentTransactionStatus = () => {
    dispatch(
      getTransactionStatus({
        transactionId: storage.getItem(TRANSACTION_ID),
      })
    );
  };
  const retrieveCheckoutDetails = (labels: IMapString) => {
    isOrderConfirmationPage.current = true;

    const onSuccessCallback = (orderData: IConfirmationOrderDetailsResponse) => {
      const { paymentInfo, packingCharges, sustainabilityConsent, totalBundleDiscount } = orderData;

      const storedOrderDetail = !isNewOrderConfirmationApi
        ? sessStorage.getItem(constants.orderDetails)
        : '';
      if (!storedOrderDetail && !isNewOrderConfirmationApi) {
        window.location.replace(window.location.origin);
        return;
      }
      const orderDetail = storedOrderDetail ? JSON.parse(storedOrderDetail) : {};
      if (!isNewOrderConfirmationApi && paymentInfo) {
        orderDetail.paymentInfo = {
          ...paymentInfo,
        };
      }
      const transformedData = isNewOrderConfirmationApi
        ? transformData(orderData, labels)
        : transformData(
            {
              ...orderDetail,
              ...{ packingCharges, sustainabilityConsent, totalBundleDiscount },
            },
            labels
          );

      const storedOrderData: any = JSON.parse(
        storage.getItem(ORDER_CONFIRMATION_DETAILS_KEY) || '{}'
      );
      const {
        purchasedProductData = {
          gaProductList: [],
        },
        purchasedGa4ProductData = '',
      } = transformedData;
      dispatch(hydrateReducerWithOrder(transformedData));
      dispatch(orderDetailLoading(false));
      clearSessionStorage();
      /**
       * @description - Old Purchase event - ga4=false - Need this when Analytic team requests back
       * @deprecated
       */
      // emitPurchaseEvent(orderData, purchasedProductData, storedOrderData.loyalty, false);
      if (adtechCretioEnabled) {
        const dispatchAction =
          (paymentArchitecture && paymentArchitecture.pv) === EPaymentArchitectureTypes.V3
            ? trackTransactionV3
            : trackTransaction;
        dispatch(dispatchAction(orderData.code));
      }
      /**
       * @description - Old Purchase event - ga4=true - Need this when Analytic team requests back
       * @deprecated
       */
      // emitPurchaseEvent(orderData, purchasedGa4ProductData, storedOrderData.loyalty, true);
      // @ts-ignore
      emitNewPurchaseEvent(transformedData, purchasedGa4ProductData);
      if (isSponsoredOrderReportingEnabled) {
        dispatch(
          sendOrderIdToCitrus(
            transformCitrusData(currencyRate, purchasedProductData.gaProductList, orderID)
          )
        );
      }

      if (
        isOrderConfirmationPage.current &&
        !isRRTrackerUpdated.current &&
        Object.keys(purchasedProductData).length
      ) {
        const {
          productIDs: productIdsList = [],
          prices: productPrices = [],
          quantities: productQuantity = [],
        } = purchasedGa4ProductData || {};
        isRRTrackerUpdated.current = true;
        sendRRTrackingEvent({ productIdsList, productPrices, productQuantity });
      }
    };
    const onFailureCallback = () => {
      dispatch(orderDetailLoading(false));
      const transformedData = getBasicOrderDetailFromStorage();
      dispatch(hydrateReducerWithOrder(transformedData));
    };
    dispatch(orderDetailLoading(true));
    dispatch(
      fetchOrderDetailsFor({
        currentOrderID: orderID,
        onSuccessCallback,
        onFailureCallback,
      })
    );
  };

  const sendRRTrackingEvent = useCallback(
    ({
      productIdsList,
      productPrices,
      productQuantity,
    }: {
      productIdsList: string[];
      productPrices: number[];
      productQuantity: string[];
    }) => {
      dispatch(
        triggerRichRelevanceUpdate({
          productIds: productIdsList.join('|'),
          placements: ERichRelConstants.placements,
          orderNumber: orderID,
          quantity: productQuantity.join('|'),
          price: productPrices.join('|'),
          isFromCart: false,
        })
      );
    },
    [dispatch, orderID]
  );

  return {
    captureCheckoutDetails,
    getCheckoutOrderDetails,
    orderConfirmation,
    clearSessionStorage,
    retrieveCheckoutDetails,
    checkSecondaryStatus,
    checkPaymentV3Status,
    // getCheckoutOrderV3Details,
  };
}
